import { queryOptions } from '@tanstack/react-query';

import { BRAND_SERVICE_VERSION_HEADERS } from '@/constants/environments';
import apiClient from '@/constants/http';
import { retryOnServerError } from '@/utils/apiErrors';
import { Logger } from '@/utils/logging';

import type {
  ServiceCategoriesParams,
  ServiceCategoriesResponse,
  ServiceCategoryRow,
} from './types';

export const buildServiceCategoriesRequest = ({
  brandId,
  shopId,
  barberId,
}: ServiceCategoriesParams) => ({
  url: `/v2/brand/${brandId}/service-category`,
  params: {
    shopId,
    withGroups: 'true',
    ...(barberId ? { barberId } : {}),
  } as Record<string, string>,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeRow = (raw: unknown, index: number): ServiceCategoryRow => {
  if (!isRecord(raw)) {
    throw new Error(`Invalid service category at index ${index}`);
  }
  const { id, name, parentCategoryId, position, libraryKey, visible } = raw;

  if (typeof id !== 'string' || typeof name !== 'string') {
    throw new Error(`Invalid service category at index ${index}: id or name`);
  }
  if (
    parentCategoryId !== undefined &&
    parentCategoryId !== null &&
    typeof parentCategoryId !== 'string'
  ) {
    throw new Error(`Invalid service category ${id}: parentCategoryId`);
  }
  if (
    position != null &&
    !(typeof position === 'number' && Number.isFinite(position))
  ) {
    throw new Error(`Invalid service category ${id}: position`);
  }
  if (visible != null && typeof visible !== 'boolean') {
    throw new Error(`Invalid service category ${id}: visible`);
  }

  return {
    id,
    name,
    parentCategoryId: parentCategoryId ?? null,
    position: position ?? 0,
    libraryKey: typeof libraryKey === 'string' ? libraryKey : null,
    visible: visible ?? true,
  };
};

export const normalizeServiceCategoriesResponse = (
  data: unknown,
): ServiceCategoriesResponse => {
  const rawRows = Array.isArray(data)
    ? data
    : isRecord(data) && Array.isArray(data.rows)
      ? data.rows
      : null;

  if (!rawRows) {
    throw new Error('Invalid service categories response: expected rows');
  }

  const rows = rawRows.map(normalizeRow);
  const count =
    isRecord(data) && typeof data.count === 'number' ? data.count : rows.length;

  return { count, rows };
};

export const fetchServiceCategories = async (
  categoriesParams: ServiceCategoriesParams,
  signal?: AbortSignal,
): Promise<ServiceCategoriesResponse> => {
  const { url, params } = buildServiceCategoriesRequest(categoriesParams);
  try {
    const { data } = await apiClient.get<unknown>(url, {
      params,
      headers: BRAND_SERVICE_VERSION_HEADERS,
      signal,
    });
    return normalizeServiceCategoriesResponse(data);
  } catch (error) {
    Logger.error('Failed to fetch service categories', {
      error,
      ...categoriesParams,
    });
    throw error;
  }
};

export const getServiceCategoriesQuery = (params: ServiceCategoriesParams) =>
  queryOptions({
    enabled: Boolean(params.brandId) && Boolean(params.shopId),
    queryKey: [
      'booking',
      'service-categories',
      params.brandId,
      params.shopId,
      params.barberId ?? 'any',
    ],
    queryFn: ({ signal }) => fetchServiceCategories(params, signal),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: retryOnServerError,
  });
